import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../services/ApiService';
import Loader from './Loader';
import Paginator from './Paginator';
import { formatDate } from '../utils/helper';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { selectStoredPermissions } from '../stores/permissionsSlice';

const statusBadge = {
    Pending: 'warning',
    Approved: 'success',
    Rejected: 'danger',
};

const escapeHtml = (v = '') => String(v).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Facility picker for a property: facilities covering its area are listed first
const facilitySelectHtml = (facilities, selectedId) => {
    const option = f => `<option value="${f._id}" ${String(f._id) === String(selectedId) ? 'selected' : ''}>${escapeHtml(f.name)}</option>`;
    const covering = facilities.filter(f => f.covers_area);
    const others = facilities.filter(f => !f.covers_area);
    return `
        <select id="swal-facility" class="form-select mb-2">
            <option value="">Select facility</option>
            ${covering.length ? `<optgroup label="Covers this area">${covering.map(option).join('')}</optgroup>` : ''}
            ${others.length ? `<optgroup label="${covering.length ? 'Other facilities' : 'All facilities'}">${others.map(option).join('')}</optgroup>` : ''}
        </select>
        <div class="text-muted fs-7 text-start">The user will see this facility's services for the property.</div>`;
};

// Lists properties. Pass `userId` to scope to one user (hides the owner column).
const PropertiesTable = ({ userId = null, defaultStatus = '' }) => {
    const permissions = selectStoredPermissions() || [];
    const canUpdate = permissions.length === 0 || permissions.includes('update-properties-status');

    const [items, setItems] = useState([]);
    const [isLoader, setIsLoader] = useState(false);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({
        search: '',
        status: defaultStatus,
        page: 1,
    });

    const fetchData = async () => {
        setIsLoader(true);
        try {
            const params = { ...apiData, limit: 10 };
            if (userId) params.user_id = userId;
            const { data } = await ApiService.request({
                method: 'GET',
                url: '/properties',
                data: params,
            });
            if (data.status) {
                setItems(data.data);
                setPagination(data.pagination);
            }
        } finally {
            setIsLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [userId, apiData.page, apiData.search, apiData.status]);

    const loadFacilities = async (id) => {
        const { data } = await ApiService.request({ method: 'GET', url: `/properties/${id}/facilities` });
        if (!data.status) {
            toast.error(data.message);
            return null;
        }
        if (!data.data.length) {
            toast.error('No active facility found. Add a facility first.');
            return null;
        }
        return data.data;
    };

    const approve = async (item) => {
        let facilities;
        try {
            facilities = await loadFacilities(item._id);
        } catch (error) {
            return; // ApiService already shows the error toast
        }
        if (!facilities) return;

        const result = await Swal.fire({
            title: 'Approve Property?',
            html: `
                <div class="text-start mb-2 fw-semibold">${escapeHtml(item.building_name)}, Unit ${escapeHtml(item.unit_no)} — ${escapeHtml(item.area)}</div>
                <label class="form-label text-start w-100 required mt-3">Assign facility</label>
                ${facilitySelectHtml(facilities)}
                <textarea id="swal-notes" class="form-control mt-4" rows="3" placeholder="Add admin notes (optional)"></textarea>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, approve it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
            preConfirm: () => {
                const facility_id = document.getElementById('swal-facility').value;
                if (!facility_id) {
                    Swal.showValidationMessage('Please select a facility');
                    return false;
                }
                return { facility_id, admin_notes: document.getElementById('swal-notes').value };
            },
        });
        if (!result.isConfirmed) return;
        try {
            const { data } = await ApiService.request({ method: 'POST', url: `/properties/${item._id}/approve`, data: result.value });
            if (data.status) {
                toast.success(data.message);
                fetchData();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // ApiService already shows the error toast
        }
    };

    const changeFacility = async (item) => {
        let facilities;
        try {
            facilities = await loadFacilities(item._id);
        } catch (error) {
            return; // ApiService already shows the error toast
        }
        if (!facilities) return;

        const result = await Swal.fire({
            title: item.facility_id ? 'Change Facility' : 'Assign Facility',
            html: facilitySelectHtml(facilities, item.facility_id?._id),
            showCancelButton: true,
            confirmButtonText: 'Save',
            cancelButtonColor: '#9E9B9B',
            preConfirm: () => {
                const facility_id = document.getElementById('swal-facility').value;
                if (!facility_id) {
                    Swal.showValidationMessage('Please select a facility');
                    return false;
                }
                return { facility_id };
            },
        });
        if (!result.isConfirmed) return;
        try {
            const { data } = await ApiService.request({ method: 'POST', url: `/properties/${item._id}/facility`, data: result.value });
            if (data.status) {
                toast.success(data.message);
                fetchData();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // ApiService already shows the error toast
        }
    };

    const reject = async (id) => {
        const result = await Swal.fire({
            title: 'Reject Property?',
            text: 'Are you sure you want to reject this property?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, reject it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
            input: 'textarea',
            inputPlaceholder: 'Add rejection reason (optional)',
        });

        if (!result.isConfirmed) return;
        try {
            const { data } = await ApiService.request({
                method: 'POST',
                url: `/properties/${id}/reject`,
                data: { admin_notes: result.value || '' },
            });
            if (data.status) {
                toast.success(data.message);
                fetchData();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // ApiService already shows the error toast
        }
    };

    const deleteProperty = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Property?',
            text: 'This property will be removed from the user account.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
        });
        if (!result.isConfirmed) return;
        try {
            const { data } = await ApiService.request({ method: 'DELETE', url: `/properties/${id}` });
            if (data.status) {
                toast.success(data.message);
                fetchData();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // ApiService already shows the error toast
        }
    };

    const colSpan = userId ? 9 : 10;

    return (
        <>
            <div className="row mb-5">
                <div className="col-md-4">
                    <input
                        type="text"
                        value={apiData.search}
                        onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                        className="form-control form-control-solid"
                        placeholder={userId ? 'Search by building, unit, area...' : 'Search by building, unit, area, user...'}
                    />
                </div>
                <div className="col-md-3">
                    <select
                        className="form-select form-select-solid"
                        value={apiData.status}
                        onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}
                    >
                        <option value="">All Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                    </select>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table align-middle table-hover fs-6 gy-5">
                    <thead>
                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>Building</th>
                            {!userId && <th>User</th>}
                            <th>Area / Community</th>
                            <th>Facility</th>
                            <th>Unit Type</th>
                            <th>Contact</th>
                            <th>Ejari</th>
                            <th>Status</th>
                            <th>Submitted On</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600 fs-6">
                        {items.length > 0 ? items.map(item => (
                            <tr key={item._id}>
                                <td>
                                    <div className="fw-bold text-dark">{item.building_name}</div>
                                    <div className="text-muted fs-7">Unit {item.unit_no}</div>
                                </td>
                                {!userId && (
                                    <td>
                                        {item.user_id ? (
                                            <Link to={`/users/${item.user_id._id}`} className="text-hover-primary">
                                                <div className="fw-bold text-dark">{item.user_id.name || '-'}</div>
                                                <div className="text-muted fs-7">{item.user_id.email || '-'}</div>
                                            </Link>
                                        ) : '-'}
                                    </td>
                                )}
                                <td>{item.area}</td>
                                <td>
                                    {item.facility_id ? (
                                        <Link to={`/facilities/${item.facility_id._id}`} className="fw-bold text-dark text-hover-primary">
                                            {item.facility_id.name}
                                        </Link>
                                    ) : <span className="text-muted">{item.status === 'Approved' ? 'Not assigned' : '-'}</span>}
                                    {canUpdate && item.status === 'Approved' && (
                                        <button onClick={() => changeFacility(item)} className="btn btn-link btn-sm p-0 d-block fs-7">
                                            {item.facility_id ? 'Change' : 'Assign'}
                                        </button>
                                    )}
                                </td>
                                <td><span className="badge badge-light-success">{item.unit_type}</span></td>
                                <td>{item.phone_code} {item.contact_number}</td>
                                <td>
                                    {item.ejari_file_url ? (
                                        <a href={item.ejari_file_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light-primary">
                                            <i className="bi bi-file-earmark-text"></i> View
                                        </a>
                                    ) : '-'}
                                </td>
                                <td>
                                    <span className={`badge bg-${statusBadge[item.status] || 'secondary'}`}>{item.status}</span>
                                    {item.admin_notes && (
                                        <div className="text-muted fs-8 mt-1 text-truncate" style={{ maxWidth: '160px' }} title={item.admin_notes}>
                                            {item.admin_notes}
                                        </div>
                                    )}
                                </td>
                                <td>{formatDate(item.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                <td>
                                    <div className="d-flex gap-2">
                                        {canUpdate && item.status === 'Pending' && (
                                            <>
                                                <button onClick={() => approve(item)} className="btn btn-sm btn-success" title="Approve">
                                                    <i className="bi bi-check-circle"></i>
                                                </button>
                                                <button onClick={() => reject(item._id)} className="btn btn-sm btn-danger" title="Reject">
                                                    <i className="bi bi-x-circle"></i>
                                                </button>
                                            </>
                                        )}
                                        {canUpdate && (
                                            <button onClick={() => deleteProperty(item._id)} className="btn btn-sm btn-light-danger" title="Delete">
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={colSpan} className="text-center text-muted py-8">
                                    <div className="d-flex flex-column align-items-center">
                                        <i className="bi bi-house fs-2 text-muted mb-3"></i>
                                        <span>No properties found</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isLoader && (
                <div className="d-flex justify-content-center py-8">
                    <Loader text="Loading properties..." size="30" />
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-5">
                <span className="text-muted">Total Records: {pagination?.total_records || 0}</span>
                {pagination?.total_pages > 1 && (
                    <Paginator
                        page={pagination.current_page}
                        rows={pagination.per_page}
                        totalRecords={pagination.total_records}
                        onPageChange={page => setApiData(prev => ({ ...prev, page }))}
                    />
                )}
            </div>
        </>
    );
};

export default PropertiesTable;
